import { generateApolloClient } from "@/common/apolloClientUtil";
import { getCityByPrefGroupedByStepA } from "@/common/graphql/cityByPref";
import { getSectionByPrefCityGroupedByStepA } from "@/common/graphql/sectionByPrefCity";
import { localStorageGetCustomerCode } from "@/common/localStorageUtil";
import {
  GetBlockByPrefCitySectionForShopRegisterDocument,
  type GetBlockByPrefCitySectionForShopRegisterQuery,
  GetBlockByZipCodeForShopRegisterDocument,
  type GetBlockByZipCodeForShopRegisterQuery,
  GetPrefCitySectionByZipCodeForShopRegisterDocument,
  type GetPrefCitySectionByZipCodeForShopRegisterQuery,
  GetZipCodeByPrefCitySectionDocument,
  type GetZipCodeByPrefCitySectionQuery,
} from "@/generated/graphql";
import type { ApolloQueryResult } from "@apollo/client/core";
import type {
  AddressBlockItem,
  AddressResolvedPayload,
  AddressSearchByZipResult,
  AddressSelectorProvider,
  AddressSelectorQueryInput,
  KanaStepA,
} from "../types";

/** 店舗住所選択の反映先を表す型。 */
type ShopAddressWritable = {
  chain_code: string;
  zip_code: string;
  address1: string;
};

/** 店舗住所選択プロバイダに必要な依存をまとめた型。 */
export interface ShopRegisterAddressProviderContext {
  shopCreateInput: ShopAddressWritable;
  chainCode?: string;
  ensureChainCode?: () => string;
}

/** カナ行一覧の空データを生成する。 */
const createEmptyKanaStepA = (): KanaStepA => ({
  line_a: [],
  line_ka: [],
  line_sa: [],
  line_ta: [],
  line_na: [],
  line_ha: [],
  line_ma: [],
  line_ya: [],
  line_ra: [],
  line_wa: [],
  line_other: [],
});

/** 先頭カナから五十音行キーを判定する。 */
const resolveKanaLineKey = (
  kanaHead: string
): keyof Pick<
  KanaStepA,
  | "line_a"
  | "line_ka"
  | "line_sa"
  | "line_ta"
  | "line_na"
  | "line_ha"
  | "line_ma"
  | "line_ya"
  | "line_ra"
  | "line_wa"
  | "line_other"
> => {
  if (["ｱ", "ｲ", "ｳ", "ｴ", "ｵ"].includes(kanaHead)) {
    return "line_a";
  }
  if (["ｶ", "ｷ", "ｸ", "ｹ", "ｺ"].includes(kanaHead)) {
    return "line_ka";
  }
  if (["ｻ", "ｼ", "ｽ", "ｾ", "ｿ"].includes(kanaHead)) {
    return "line_sa";
  }
  if (["ﾀ", "ﾁ", "ﾂ", "ﾃ", "ﾄ"].includes(kanaHead)) {
    return "line_ta";
  }
  if (["ﾅ", "ﾆ", "ﾇ", "ﾈ", "ﾉ"].includes(kanaHead)) {
    return "line_na";
  }
  if (["ﾊ", "ﾋ", "ﾌ", "ﾍ", "ﾎ"].includes(kanaHead)) {
    return "line_ha";
  }
  if (["ﾏ", "ﾐ", "ﾑ", "ﾒ", "ﾓ"].includes(kanaHead)) {
    return "line_ma";
  }
  if (["ﾔ", "ﾕ", "ﾖ"].includes(kanaHead)) {
    return "line_ya";
  }
  if (["ﾗ", "ﾘ", "ﾙ", "ﾚ", "ﾛ"].includes(kanaHead)) {
    return "line_ra";
  }
  if (kanaHead === "ﾜ") {
    return "line_wa";
  }
  return "line_other";
};

/** chain_code の優先順を統一して解決する。 */
const resolveChainCode = (context?: ShopRegisterAddressProviderContext): string => {
  if (context?.shopCreateInput?.chain_code) {
    return context.shopCreateInput.chain_code;
  }
  if (context?.chainCode) {
    return context.chainCode;
  }
  if (context?.ensureChainCode) {
    return context.ensureChainCode();
  }
  throw new Error("chain_code が取得できません。");
};

/** 郵便番号検索結果を市区/町域/丁目に分類する。 */
const classifyZipSearch = (
  rows: NonNullable<GetBlockByZipCodeForShopRegisterQuery["getBlockByZipCodeForShopRegister"]>
): Pick<AddressSearchByZipResult, "cityBlockList" | "sectionBlockList" | "blockList"> => {
  if (rows.length === 0) {
    return {
      cityBlockList: null,
      sectionBlockList: null,
      blockList: [],
    };
  }

  let prevCity = "";
  let prevSection = "";
  let cityFixed = true;
  let sectionFixed = true;

  for (const row of rows) {
    if (!row?.section_name) {
      continue;
    }

    if (!prevCity) {
      prevCity = row.city_name;
    } else if (prevCity !== row.city_name) {
      cityFixed = false;
    }

    if (!prevSection) {
      prevSection = row.section_name;
    } else if (prevSection !== row.section_name) {
      sectionFixed = false;
    }
  }

  if (cityFixed && sectionFixed) {
    const blockList: AddressBlockItem[] = [];
    let hasOther = false;

    for (const row of rows) {
      if (!row) {
        continue;
      }
      if (!row.block_name || row.block_name === "") {
        hasOther = true;
      } else {
        blockList.push({
          name: row.block_name,
          enabled: true,
        });
      }
    }

    if (hasOther) {
      blockList.push({ name: "その他", enabled: true });
    }

    return {
      cityBlockList: null,
      sectionBlockList: null,
      blockList,
    };
  }

  if (cityFixed) {
    const sectionBlockList = createEmptyKanaStepA();
    const sectionNames = new Set<string>();
    for (const row of rows) {
      if (!row?.section_name) {
        continue;
      }
      const sectionName = row.section_name.replace("　", "");
      if (sectionNames.has(sectionName)) {
        continue;
      }
      sectionNames.add(sectionName);
      const kanaHead = row.section_kana?.substring(0, 1) ?? "";
      const lineKey = resolveKanaLineKey(kanaHead);
      sectionBlockList[lineKey]?.push(sectionName);
    }
    return {
      cityBlockList: null,
      sectionBlockList,
      blockList: [],
    };
  }

  const cityBlockList = createEmptyKanaStepA();
  const cityNames = new Set<string>();
  for (const row of rows) {
    if (!row?.city_name) {
      continue;
    }
    const cityName = row.city_name.replace("　", "");
    if (cityNames.has(cityName)) {
      continue;
    }
    cityNames.add(cityName);
    const kanaHead = row.city_kana?.substring(0, 1) ?? "";
    const lineKey = resolveKanaLineKey(kanaHead);
    cityBlockList[lineKey]?.push(cityName);
  }

  return {
    cityBlockList,
    sectionBlockList: null,
    blockList: [],
  };
};

/** 店舗登録向け住所選択プロバイダを作成する。 */
export const createShopRegisterAddressProvider = (): AddressSelectorProvider<ShopRegisterAddressProviderContext> => {
  return {
    /** 郵便番号の入力値を検証する。 */
    validateZip(zipCode: string): string | null {
      if (zipCode.length < 7) {
        return "郵便番号が入力されていません。";
      }
      return null;
    },

    /** 郵便番号から住所候補を取得する。 */
    async searchByZip(input, context): Promise<AddressSearchByZipResult> {
      const apolloClient = await generateApolloClient();
      const zipCode = input.zipCode ?? "";
      const chainCode = resolveChainCode(context);

      const prefResult: ApolloQueryResult<GetPrefCitySectionByZipCodeForShopRegisterQuery> =
        await apolloClient.query<GetPrefCitySectionByZipCodeForShopRegisterQuery>({
          query: GetPrefCitySectionByZipCodeForShopRegisterDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: chainCode,
            zip_code: zipCode,
          },
          fetchPolicy: "no-cache",
        });

      const prefData = prefResult.data.getPrefCitySectionByZipCodeForShopRegister ?? [];
      if (prefData.length === 0) {
        return {
          cityBlockList: null,
          sectionBlockList: null,
          blockList: [],
        };
      }

      const blockResult: ApolloQueryResult<GetBlockByZipCodeForShopRegisterQuery> =
        await apolloClient.query<GetBlockByZipCodeForShopRegisterQuery>({
          query: GetBlockByZipCodeForShopRegisterDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: chainCode,
            zip_code: zipCode,
          },
          fetchPolicy: "no-cache",
        });

      const rows = blockResult.data.getBlockByZipCodeForShopRegister ?? [];
      if (rows.length === 0) {
        return {
          cityBlockList: null,
          sectionBlockList: null,
          blockList: [],
        };
      }

      if (rows.length === 1 && rows[0]) {
        const row = rows[0];
        return {
          cityBlockList: null,
          sectionBlockList: null,
          blockList: [],
          selectedPref: row.pref_name,
          selectedCity: row.city_name.replace("　", ""),
          selectedSection: row.section_name.replace("　", ""),
          resolved: {
            zip_code: zipCode,
            address1:
              row.pref_name +
              row.city_name.replace("　", "") +
              row.section_name.replace("　", "") +
              (row.block_name ?? ""),
            pref_name: row.pref_name,
            city_name: row.city_name.replace("　", ""),
            section_name: row.section_name.replace("　", ""),
            block_name: row.block_name ?? "",
          },
        };
      }

      const classified = classifyZipSearch(rows);
      return {
        ...classified,
        selectedPref: rows[0]?.pref_name ?? "",
        // 郵便番号検索で丁目候補が返るケースでも市区町村を保持する。
        selectedCity:
          classified.sectionBlockList !== null || classified.blockList.length > 0
            ? rows[0]?.city_name?.replace("　", "") ?? ""
            : "",
        selectedSection:
          classified.blockList.length > 0
            ? rows[0]?.section_name?.replace("　", "") ?? ""
            : "",
      };
    },

    /** 都道府県から市区候補を取得する。 */
    async getCityListByPref(input, context): Promise<KanaStepA | null> {
      const chainCode = resolveChainCode(context);
      return (
        (await getCityByPrefGroupedByStepA(
        localStorageGetCustomerCode(),
        chainCode,
        "",
        input.pref ?? ""
      )) ?? null
      );
    },

    /** 都道府県と市区から町域候補を取得する。 */
    async getSectionListByPrefCity(input, context): Promise<KanaStepA | null> {
      const chainCode = resolveChainCode(context);
      return (
        (await getSectionByPrefCityGroupedByStepA(
        localStorageGetCustomerCode(),
        chainCode,
        "",
        input.pref ?? "",
        input.city ?? ""
      )) ?? null
      );
    },

    /** 都道府県・市区・町域から丁目候補を取得する。 */
    async getBlockListByPrefCitySection(input, context): Promise<AddressBlockItem[]> {
      const apolloClient = await generateApolloClient();
      const chainCode = resolveChainCode(context);
      const section = input.section === "その他" ? "" : input.section ?? "";

      const result: ApolloQueryResult<GetBlockByPrefCitySectionForShopRegisterQuery> =
        await apolloClient.query<GetBlockByPrefCitySectionForShopRegisterQuery>({
          query: GetBlockByPrefCitySectionForShopRegisterDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: chainCode,
            pref: input.pref ?? "",
            city: input.city ?? "",
            section,
          },
          fetchPolicy: "no-cache",
        });

      const rawBlocks = result.data.getBlockByPrefCitySectionForShopRegister ?? [];
      return rawBlocks.map((block) => ({
        // 既存店舗登録画面と同じく、空文字は空文字のまま返す。
        name: block,
        enabled: true,
      }));
    },

    /** 都道府県・市区・町域から郵便番号を解決する。 */
    async resolveZipByPrefCitySection(input, context): Promise<AddressResolvedPayload | null> {
      const apolloClient = await generateApolloClient();
      const chainCode = resolveChainCode(context);
      const section = input.section === "その他" ? "" : input.section ?? "";

      const result: ApolloQueryResult<GetZipCodeByPrefCitySectionQuery> =
        await apolloClient.query<GetZipCodeByPrefCitySectionQuery>({
          query: GetZipCodeByPrefCitySectionDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: chainCode,
            pref: input.pref ?? "",
            city: input.city ?? "",
            section,
          },
          fetchPolicy: "no-cache",
        });

      const zipCode = result.data.getZipCodeByPrefCitySection;
      if (!zipCode) {
        return null;
      }

      return {
        zip_code: zipCode,
        address1: (input.pref ?? "") + (input.city ?? "") + section,
        pref_name: input.pref,
        city_name: input.city,
        section_name: section,
        block_name: "",
      };
    },

    /** 都道府県・市区・町域・丁目から郵便番号を解決する。 */
    async resolveZipByPrefCitySectionBlock(input, context): Promise<AddressResolvedPayload | null> {
      // 既存店舗登録では block 指定での再解決を行わないため、section 解決値を利用する。
      const sectionResolved = await this.resolveZipByPrefCitySection(input, context);
      if (!sectionResolved) {
        return null;
      }

      const block = input.block === "その他" ? "" : input.block ?? "";
      return {
        ...sectionResolved,
        block_name: block,
        address1: sectionResolved.address1 + block,
      };
    },

    /** 解決済み住所を店舗入力モデルへ反映する。 */
    applyResolvedAddress(payload, context): void {
      if (!context) {
        return;
      }

      context.shopCreateInput.address1 = payload.address1;

      if (!context.shopCreateInput.chain_code) {
        context.shopCreateInput.chain_code = resolveChainCode(context);
      }
    },
  };
};
